import React, { useEffect, useState } from 'react';
import { BoxGeometry, Material, Mesh, Quaternion, Vector3 } from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

import { Direction2D, LineSegment2D, Point2D, Vector2D } from '@paragon/geometry-library';
import { newVector3, RenderOrder, Segment2D } from '@paragon/three-shared';

import { useAppSelector } from '../../components/hooks';
import { is3DLayoutCameraInUse } from '../../store/selectors/layoutModeSelectors';
import { subscribeToAntiZoomFactor } from '../antiZoom';
import { getActiveCamera } from '../reactThreeFiberBindings';
import { LabelVisual, LabelVisualProps } from '../util/LabelVisual';
import { DimensionLabelOptions, DimensionStyleVariant, useDimensionStyle } from './dimensionStyles';
import {
  buildEndpointMarkSegments,
  EndpointMark,
  EndpointMarkConfiguration,
  getEndpointMarkConfiguration,
} from './endpointMarks';

const solidBodyGeometry = new BoxGeometry(1, 1, 1);

const DIMENSION_LABEL_OFFSET_RATIO = 0.7;
const DEFAULT_DIMENSION_THICKNESS = 0.2;
const Y_AXIS = new Vector3(0, 1, 0);

function buildDashedGeometry(segment: Segment2D) {
  const distance = Point2D.fromObject(segment.start).distanceTo(Point2D.fromObject(segment.end));
  const dashedRatio = distance * 2;

  const boxGeometries: BoxGeometry[] = [];

  const ceiledDistance = Math.ceil(distance);
  for (let i = 0; i < ceiledDistance; i += 1) {
    // shift last box vertices to edge of distance line
    const shiftAmount =
      i === ceiledDistance - 1 && ceiledDistance - distance > 0.1 ? (ceiledDistance - distance) / dashedRatio : 0;
    const boxGeometry = new BoxGeometry(1, 1 / dashedRatio - shiftAmount, 1);
    boxGeometry.translate(0, i / distance - (0.5 - 0.5 / distance) - shiftAmount / 2, 0);
    boxGeometries.push(boxGeometry);
  }

  return mergeGeometries(boxGeometries);
}

export interface DimensionLabelData {
  readonly firstNumber?: number;
  readonly secondNumber?: number;
  readonly fontSize?: number;
  readonly applyAntiZoomFactor?: boolean;
  readonly labelOnePosition?: Point2D;
  readonly labelTwoPosition?: Point2D;
}

export interface DistanceDimensionProps {
  // The end points of the `segment` could be identical, which is why it's not passed as a `LineSegment2D`.
  readonly segment: Segment2D;
  readonly dashed?: boolean;
  readonly styleVariant?: DimensionStyleVariant;
  readonly thickness?: number;
  readonly showEndpointMarks?: boolean;
  readonly textFloat?: number;
  readonly distanceFormatter?: (lengthInInches: number) => string;
  readonly labelData?: DimensionLabelData;
  readonly labelRenderOrder?: RenderOrder;
  readonly orientText?: boolean;
  readonly label1Callback?: (troika: Mesh) => void;
  readonly label2Callback?: (troika: Mesh) => void;
}

export function DistanceDimension(props: DistanceDimensionProps) {
  const { material, labelOptions } = useDimensionStyle(props.styleVariant ?? 'standard');
  const thickness = props.thickness ?? DEFAULT_DIMENSION_THICKNESS;
  const dashedGeometry = props.dashed ? buildDashedGeometry(props.segment) : null;
  useEffect(() => () => void dashedGeometry?.dispose());

  const bodyGeometry = dashedGeometry ?? solidBodyGeometry;

  const startPoint = Point2D.fromObject(props.segment.start);
  const endPoint = Point2D.fromObject(props.segment.end);
  const vector = Vector2D.fromPointAndPoint(startPoint, endPoint);
  const center = startPoint.translate(vector.divideScalar(2));

  const showEndpointMarks = props.showEndpointMarks && !startPoint.equals(endPoint);

  return (
    <object3D>
      <mesh
        geometry={bodyGeometry}
        material={material}
        position={center.to3D().toArray()}
        quaternion={quaternionAlignedWith(vector.direction).toArray()}
        scale={[thickness, vector.magnitude, thickness]}
        renderOrder={RenderOrder.DistanceDimensionLineOverlay}
      />

      {showEndpointMarks ? (
        <EndpointMarkMeshes
          dimensionSegment={new LineSegment2D(startPoint, endPoint)}
          thickness={thickness}
          material={material}
        />
      ) : null}

      {props.labelData ? (
        <DistanceDimensionLabels
          segment={props.segment}
          textFloat={props.textFloat}
          distanceFormatter={props.distanceFormatter}
          labelData={props.labelData}
          labelOptions={labelOptions}
          labelRenderOrder={props.labelRenderOrder}
          orientText={props.orientText}
          label1Callback={props.label1Callback}
          label2Callback={props.label2Callback}
        />
      ) : null}
    </object3D>
  );
}

interface EndpointMarkTransform {
  readonly position: Vector3;
  readonly quaternion: Quaternion;
  readonly scale: Vector3;
}

interface EndpointMarkMeshesProps {
  readonly dimensionSegment: LineSegment2D;
  readonly thickness: number;
  readonly material: Material;
}

function EndpointMarkMeshes({ dimensionSegment, thickness, material }: EndpointMarkMeshesProps) {
  const configuration = useAppSelector(getEndpointMarkConfiguration);
  const transforms = buildEndpointMarkTransforms(dimensionSegment, thickness, configuration);

  return (
    <>
      {transforms.map((mark, index) => (
        <mesh
          key={index}
          geometry={solidBodyGeometry}
          material={material}
          position={mark.position.toArray()}
          quaternion={mark.quaternion.toArray()}
          scale={mark.scale.toArray()}
          renderOrder={RenderOrder.DistanceDimensionLineOverlay}
        />
      ))}
    </>
  );
}

function buildEndpointMarkTransforms(
  dimensionSegment: LineSegment2D,
  thickness: number,
  configuration: EndpointMarkConfiguration,
): readonly EndpointMarkTransform[] {
  const { ticks, extensionMarks } = buildEndpointMarkSegments(dimensionSegment, configuration);

  return [...buildBarTransforms(ticks, thickness), ...buildBarTransforms(extensionMarks, thickness)];
}

function buildBarTransforms(marks: readonly EndpointMark[], thickness: number): readonly EndpointMarkTransform[] {
  const [firstMark] = marks;
  if (!firstMark) return [];

  const quaternion = quaternionAlignedWith(firstMark.direction);
  const scale = new Vector3(thickness, firstMark.length, thickness);

  return marks.map((mark) => ({ position: newVector3(mark.center.to3D()), quaternion, scale }));
}

function quaternionAlignedWith(direction: Direction2D) {
  return new Quaternion().setFromUnitVectors(Y_AXIS, new Vector3(direction.x, direction.y, 0));
}

function getFontSize(fontSize?: number) {
  return fontSize ?? 5;
}

export function calculateLabelPositions(
  segment: Segment2D,

  options: {
    /** Whether to orient the text to be right-side up. Defaults to `false`. */
    orientText?: boolean | undefined;

    /** The distance to offset the label up or down. Defaults to no offset. */
    textFloat?: number | undefined;

    /** Whether to orient the label perpendicular to the dimension's direction. Defaults to `false`. */
    perpendicularToDimension?: boolean | undefined;

    /** Self explanatory. Needed for correct label placement. Defaults to 5. */
    fontSize?: number | undefined;

    /** Also needed for correct label placement. Required. */
    antiZoomFactor: number;

    /** Traditionally with 3D cameras we place labels to the right. Set to enforce this. Defaults to `false`. */
    in3D?: boolean;
  },
) {
  const startPoint = Point2D.fromObject(segment.start);
  const vector = Vector2D.fromPointAndPoint(startPoint, segment.end);
  const center = startPoint.translate(vector.divideScalar(2));
  const length = vector.magnitude;

  let direction = options.in3D ? Direction2D.right : vector.direction;

  if (options.perpendicularToDimension) {
    direction = direction.rotateQuarterTurn();
  }

  if (options.orientText) {
    const isVertical = direction.equals(Direction2D.down) || direction.equals(Direction2D.up);
    const isNegative = isVertical ? direction.y < 0 : direction.x < 0;
    if (isNegative) {
      direction = direction.reverse();
    }
  }

  if (!direction.hasDirection()) direction = Direction2D.right;

  const rotation = direction.angleInRadians;

  let standardOffset = Vector2D.zero;
  if (!options.perpendicularToDimension) {
    standardOffset = Vector2D.fromDirectionAndMagnitude(
      direction.rotateQuarterTurn(),
      getFontSize(options.fontSize) * DIMENSION_LABEL_OFFSET_RATIO * options.antiZoomFactor,
    );
  }

  let textFloatOffset = Vector2D.zero;
  if (options.textFloat) {
    // FIXME: `textFloat` is applied both as the offset's length and as a scalar multiplier, so the label ends up
    //   `textFloat` squared away from the dimension (and always on the positive side).
    const floatDirection = options.perpendicularToDimension ? direction : direction.rotateQuarterTurn();
    textFloatOffset = Vector2D.fromDirectionAndMagnitude(floatDirection, options.textFloat * options.textFloat);
  }

  const labelOnePosition = center.translate(standardOffset).translate(textFloatOffset);
  const labelTwoPosition = center.translate(standardOffset.reverse()).translate(textFloatOffset.reverse());

  return { labelOnePosition, labelTwoPosition, length, rotation };
}

interface DistanceDimensionLabelsProps {
  readonly segment: Segment2D;
  readonly textFloat?: number;
  readonly distanceFormatter?: (lengthInInches: number) => string;
  readonly labelData: DimensionLabelData;
  readonly labelOptions: DimensionLabelOptions;
  readonly labelRenderOrder?: RenderOrder;
  readonly orientText?: boolean;
  readonly label1Callback?: (troika: Mesh) => void;
  readonly label2Callback?: (troika: Mesh) => void;
}

export function DistanceDimensionLabels(props: DistanceDimensionLabelsProps) {
  const in3D = useAppSelector(is3DLayoutCameraInUse);

  const [antiZoomFactor, setAntiZoomFactor] = useState<number>(() =>
    props.labelData.applyAntiZoomFactor ? getActiveCamera().getAntiZoomFactor() : 1,
  );

  useEffect(() => {
    if (!props.labelData.applyAntiZoomFactor) return;

    return subscribeToAntiZoomFactor(() => {
      setAntiZoomFactor(getActiveCamera().getAntiZoomFactor());
    });
  }, [props.labelData.applyAntiZoomFactor]);

  const fontSize = getFontSize(props.labelData.fontSize);
  const {
    labelOnePosition: calculatedLabelOnePosition,
    labelTwoPosition: calculatedLabelTwoPosition,
    rotation,
  } = calculateLabelPositions(
    props.segment,

    {
      orientText: props.orientText,
      textFloat: props.textFloat,
      fontSize: props.labelData.fontSize,
      antiZoomFactor: antiZoomFactor,
      in3D,
    },
  );

  const labelOnePosition = props.labelData.labelOnePosition ?? calculatedLabelOnePosition;
  const labelTwoPosition = props.labelData.labelTwoPosition ?? calculatedLabelTwoPosition;

  const { applyAntiZoomFactor, firstNumber, secondNumber } = props.labelData;
  const labelVisualProps: Omit<LabelVisualProps, 'children'> = {
    ...props.labelOptions,
    fontSize,
    rotation,
    renderOrder: props.labelRenderOrder ?? RenderOrder.GlyphVisual_StandardDimension,
    applyAntiZoomFactor,
  };

  return (
    <>
      {firstNumber && props.distanceFormatter ? (
        <LabelVisual
          {...labelVisualProps}
          position={labelOnePosition.to3D().toArray()}
          onTextSync={props.label1Callback}
        >
          {props.distanceFormatter(firstNumber)}
        </LabelVisual>
      ) : null}
      {secondNumber && props.distanceFormatter ? (
        <LabelVisual
          {...labelVisualProps}
          position={labelTwoPosition.to3D().toArray()}
          onTextSync={props.label2Callback}
        >
          {props.distanceFormatter(secondNumber)}
        </LabelVisual>
      ) : null}
    </>
  );
}
